import React, { useState, useEffect } from 'react';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import { SheetView } from '../SheetEditor';
import DocxViewer from './DocxViewer';
import { EXT_MIME, SPREADSHEET_TYPES, DOCX_TYPES } from './constants';
import { assertPdfBlob } from '../../utils/pdfResponseGuard';
import styles from './DocumentViewer.module.scss';

const PdfViewer: React.FC<{ fileUrl: string }> = ({ fileUrl }) => {
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;
        let createdUrl: string | null = null;
        setLoading(true);
        setError(null);
        setBlobUrl(null);

        if (!fileUrl) {
            setError('No file URL provided');
            setLoading(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchPdf = async () => {
            try {
                const response = await fetch(fileUrl);
                if (!response.ok) {
                    throw new Error(`Failed to fetch PDF: ${response.status}`);
                }
                const blob = await response.blob();
                // Force the blob's MIME type and verify its magic header so the iframe
                // never renders a non-PDF response as HTML.
                const pdfBlob = new Blob([blob], { type: EXT_MIME.pdf });
                await assertPdfBlob(pdfBlob);
                if (isMounted) {
                    const url = URL.createObjectURL(pdfBlob);
                    createdUrl = url;
                    setBlobUrl(url);
                    setLoading(false);
                }
            } catch (err) {
                console.error('Error fetching PDF:', err);
                if (isMounted) {
                    setError(err instanceof Error ? err.message : 'Failed to load PDF');
                    setLoading(false);
                }
            }
        };

        fetchPdf();

        return () => {
            isMounted = false;
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
    }, [fileUrl]);

    if (loading) {
        return (
            <div className={styles.loadingBox}>
                <LoadingSpinner height="5rem" color="var(--primary-text-colour)" />
            </div>
        );
    }

    if (error || !blobUrl) {
        return (
            <div className={styles.errorBox}>
                <p className={styles.errorText}>Unable to preview this PDF</p>
                {fileUrl && (
                    <div className={styles.errorButtonRow}>
                        <button
                            type="button"
                            onClick={() => window.open(fileUrl, '_blank', 'noopener,noreferrer')}
                            className={styles.errorButton}
                        >
                            Open in New Tab
                        </button>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={styles.pdfContainer}>
            <iframe
                src={blobUrl}
                title="PDF preview"
                className={styles.pdfFrame}
                // No sandbox: it would block the browser's native PDF viewer, which renders
                // correctly where pdf.js mis-rasterizes some PDFs.
            />
        </div>
    );
};

interface FileViewerProps {
    fileUrl: string;
    mimeType?: string;
}

/** Pure router — no hooks here, each branch renders its own component. */
const DocumentViewer: React.FC<FileViewerProps> = ({ fileUrl, mimeType }) => {
    if (mimeType?.startsWith('image/')) {
        return (
            <div className={styles.imageContainer}>
                <img src={fileUrl} alt="preview" className={styles.previewImage} />
            </div>
        );
    }

    if (mimeType && SPREADSHEET_TYPES.includes(mimeType)) {
        return <SheetView fileUrl={fileUrl} mimeType={mimeType} />;
    }

    if (mimeType && DOCX_TYPES.includes(mimeType)) {
        return <DocxViewer fileUrl={fileUrl} />;
    }

    return <PdfViewer fileUrl={fileUrl} />;
};

export default DocumentViewer;
